import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { useApiClient } from "../../api/ApiClientContext";
import type { DeliveredBrowseResponse, OutboundBrowseResponse, SharingDetail } from "../../api/types";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { ExplorerBreadcrumbs } from "../../components/explorer/ExplorerBreadcrumbs";
import { ExplorerLayout } from "../../components/explorer/ExplorerLayout";
import { ExplorerTable, type ExplorerRow } from "../../components/explorer/ExplorerTable";
import { ExplorerGrid } from "../../components/explorer/ExplorerGrid";
import { ExplorerToolbar } from "../../components/explorer/ExplorerToolbar";
import { ExplorerUploadStatus } from "../../components/explorer/ExplorerUploadStatus";
import { FileTree } from "../../components/explorer/FileTree";
import { NewFolderDialog } from "../../components/explorer/NewFolderDialog";
import { UploadRejectedDialog } from "../../components/explorer/UploadRejectedDialog";
import { useContextMenu } from "../../hooks/useContextMenu";
import { useExplorerUrlState } from "../../hooks/useExplorerUrlState";
import { useFileUpload } from "../../hooks/useFileUpload";
import { ApiError } from "../../api/types";
import { downloadAll } from "../../utils/downloadAll";
import {
  anyRowHasAction,
  copyNameAction,
  deleteAction,
  deleteFolderAction,
  downloadAction,
  newFolderAction,
  openAction,
  refreshAction,
  runSelectedActions,
  uploadHereAction,
  type RowAction,
} from "../../utils/explorerActions";
import { filterRowsByName, mostRecent, sortRows } from "../../utils/explorerSort";
import { depthAt, joinPath, MAX_FOLDER_DEPTH } from "../../utils/pathRules";

/**
 * The two folders staff see for a sharing, mirroring exactly what the
 * partner sees (manager decision, 2026-09-11): LEEFT_TO_PARTNER (outbound,
 * staff-managed) and PARTNER_TO_LEEFT (the partner's scanned deliveries, a
 * plain listing, read-only here). DELIVERED and REJECTED are not shown as
 * their own folders: PARTNER_TO_LEEFT is backed by `getSharingDelivered`
 * (`GET /sharings/{code}/delivered?path=`, ADR 0010, amended by ADR 0011:
 * `rejected`/`rejectedTruncated` are gone from that response - a rejected
 * upload is refused synchronously and never appears anywhere in the
 * portal).
 */
type FolderKey = "PARTNER_TO_LEEFT" | "LEEFT_TO_PARTNER";

const FOLDERS: { key: FolderKey; label: string }[] = [
  { key: "PARTNER_TO_LEEFT", label: "PARTNER_TO_LEEFT" },
  { key: "LEEFT_TO_PARTNER", label: "LEEFT_TO_PARTNER" },
];

function asFolderKey(value: string | null): FolderKey | null {
  return value === "PARTNER_TO_LEEFT" || value === "LEEFT_TO_PARTNER" ? value : null;
}

export interface StaffSharingFilesTabProps {
  code: string;
  detail: SharingDetail;
  onRefresh: () => Promise<void>;
}

/**
 * The staff-side explorer for one sharing, mirroring exactly the two
 * folders the partner sees (manager decision, 2026-09-11):
 * PARTNER_TO_LEEFT (the partner's scanned deliveries, a plain listing,
 * read-only here, through `getSharingDelivered`) and LEEFT_TO_PARTNER
 * (upload, download, delete with confirmation, and subfolders - ADR 0010
 * amendment A2, through `getSharingOutbound`). A rejected upload, in
 * either direction, is refused synchronously at upload time (ADR 0011)
 * and never appears in this view or anywhere else in the portal.
 */
export function StaffSharingFilesTab({ code, detail, onRefresh }: StaffSharingFilesTabProps) {
  const apiClient = useApiClient();
  const { folder, path, sort, dir, view, setFolder, setPath, navigate, toggleSort, setView } =
    useExplorerUrlState();
  const mount = asFolderKey(folder);
  const [received, setReceived] = useState<DeliveredBrowseResponse | null>(null);
  const [outbound, setOutbound] = useState<OutboundBrowseResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [dragOver, setDragOver] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [newFolderParent, setNewFolderParent] = useState<string | null>(null);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<string | null>(null);
  const [deleteFolderError, setDeleteFolderError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const paneMenu = useContextMenu();
  const latestRequestRef = useRef(0);

  // ADR 0010: ?folder=&path= is reactive URL state, so the path-scoped
  // listing (received or outbound) must reload on every navigation. The
  // root view keeps using `detail` (root-only, unchanged shape) instead
  // of a redundant fetch; only entering a folder needs one of the two
  // dedicated browse routes.
  const load = useCallback(async () => {
    if (!mount) {
      setReceived(null);
      setOutbound(null);
      return;
    }
    const requestId = ++latestRequestRef.current;
    setLoading(true);
    setError(null);
    try {
      if (mount === "LEEFT_TO_PARTNER") {
        const response = await apiClient.getSharingOutbound(code, path);
        if (latestRequestRef.current === requestId) {
          setOutbound(response);
        }
      } else {
        const response = await apiClient.getSharingDelivered(code, path);
        if (latestRequestRef.current === requestId) {
          setReceived(response);
        }
      }
    } catch (caught) {
      if (latestRequestRef.current === requestId) {
        setError(caught instanceof ApiError ? caught.message : "Could not load this folder.");
      }
    } finally {
      if (latestRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [apiClient, code, mount, path]);

  // Fetching data in response to a prop/URL change is exactly the effect
  // React's own docs endorse (unlike deriving state that render could
  // just compute): load() cannot run during render since it is async.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  function refreshAll() {
    void load();
    void onRefresh();
  }

  const {
    upload,
    error: uploadError,
    notice: uploadNotice,
    rejected: uploadRejected,
    retryFile,
    start,
    cancel,
    dismissRejected,
  } = useFileUpload((file, onProgress) => apiClient.uploadStaffFile(code, file, path, onProgress), refreshAll);

  // Resets the search box and the selection when the folder or path
  // changes, without an Effect (React docs: "Adjusting state when a prop
  // changes").
  const [previousLocation, setPreviousLocation] = useState(`${folder ?? ""} ${path}`);
  const currentLocation = `${folder ?? ""} ${path}`;
  if (currentLocation !== previousLocation) {
    setPreviousLocation(currentLocation);
    setSearch("");
    setSelected(new Set());
  }

  const uploadEnabled = folder === "LEEFT_TO_PARTNER" && !detail.closed;
  const newFolderEnabled = folder === "LEEFT_TO_PARTNER" && !detail.closed && depthAt(path) <= MAX_FOLDER_DEPTH;

  // A stable function value (not a fresh ref-reading closure built during
  // render) so it can be handed to row/pane actions built while rendering.
  const triggerFileInput = useCallback(() => fileInputRef.current?.click(), []);

  function uploadInto(targetPath: string) {
    if (folder !== "LEEFT_TO_PARTNER" || path !== targetPath) {
      navigate("LEEFT_TO_PARTNER", targetPath);
    }
    triggerFileInput();
  }

  function handleFileInputChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) {
      start(file);
    }
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragOver(false);
    if (!uploadEnabled || upload) {
      return;
    }
    const file = event.dataTransfer.files[0];
    if (file) {
      start(file);
    }
  }

  function toggleSelect(key: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  async function handleDeleteConfirmed() {
    if (!deleteTarget) {
      return;
    }
    setDeleteError(null);
    try {
      await apiClient.deleteOutboundFile(code, { name: deleteTarget, path });
      setDeleteTarget(null);
      refreshAll();
    } catch (caught) {
      setDeleteError(caught instanceof ApiError ? caught.message : "Could not delete the file.");
    }
  }

  async function handleCreateFolder(name: string) {
    const parent = newFolderParent ?? "";
    await apiClient.createOutboundFolder(code, joinPath(parent, name));
    setNewFolderParent(null);
    refreshAll();
  }

  async function handleDeleteFolderConfirmed() {
    if (deleteFolderTarget === null) {
      return;
    }
    setDeleteFolderError(null);
    try {
      await apiClient.deleteOutboundFolder(code, deleteFolderTarget);
      setDeleteFolderTarget(null);
      refreshAll();
    } catch (caught) {
      setDeleteFolderError(caught instanceof ApiError ? caught.message : "Could not delete the folder.");
    }
  }

  /**
   * The shared action model for a folder, wherever it appears (a root
   * mount row, a subfolder table/grid row, or a tree node): staff
   * navigate PARTNER_TO_LEEFT (read-only, the partner's own uploads) and
   * fully manage LEEFT_TO_PARTNER (ADR 0010 amendment A2). `isMountRoot`
   * is true only for the top-level row itself, never deletable.
   */
  function buildFolderActions(target: FolderKey, targetPath: string, isMountRoot: boolean): RowAction[] {
    const actions: RowAction[] = [openAction(() => navigate(target, targetPath))];
    if (target !== "LEEFT_TO_PARTNER") {
      return actions;
    }
    if (!detail.closed) {
      actions.push(uploadHereAction(() => uploadInto(targetPath)));
      if (depthAt(targetPath) <= MAX_FOLDER_DEPTH) {
        actions.push(newFolderAction(() => setNewFolderParent(targetPath)));
      }
    }
    if (!isMountRoot) {
      actions.push(deleteFolderAction(() => setDeleteFolderTarget(targetPath)));
    }
    return actions;
  }

  const rootRows: ExplorerRow[] = useMemo(() => {
    // triggerFileInput (a stable useCallback, reached through
    // buildFolderActions -> uploadInto) only runs later, from a menu
    // item's onClick; the lint rule cannot see through that call chain
    // to confirm that, hence the disable.
    // eslint-disable-next-line react-hooks/refs
    return FOLDERS.map((folderDef) => {
      // `?? []` is defensive (manager report: a BFF response can send a
      // JSON null, not [], for a nil Go slice).
      const folderFiles = (folderDef.key === "LEEFT_TO_PARTNER" ? detail.outbound : detail.delivered) ?? [];
      return {
        key: folderDef.key,
        name: folderDef.label,
        kind: "folder" as const,
        lastModified: mostRecent(folderFiles.map((f) => f.lastModified)),
        size: null,
        onActivate: () => setFolder(folderDef.key),
        actions: [...buildFolderActions(folderDef.key, "", true), refreshAction(refreshAll)],
      };
    });
  }, [detail, setFolder, buildFolderActions, refreshAll]);

  const folderRows: ExplorerRow[] = useMemo(() => {
    if (!mount) {
      return [];
    }
    if (mount === "PARTNER_TO_LEEFT") {
      if (!received) {
        return [];
      }
      // See the eslint-disable above rootRows.
      // eslint-disable-next-line react-hooks/refs
      const subfolders: ExplorerRow[] = received.folders.map((info) => {
        const childPath = joinPath(path, info.name);
        return {
          key: `folder:${info.name}`,
          name: info.name,
          kind: "folder" as const,
          lastModified: null,
          size: null,
          onActivate: () => setPath(childPath),
          actions: buildFolderActions(mount, childPath, false),
        };
      });
      // Read-only here, a plain listing (ADR 0011): download and copy name
      // only, no delete.
      const fileRows: ExplorerRow[] = received.delivered.map((file) => {
        const href = apiClient.staffDownloadUrl(code, "delivered", file.name, path);
        return {
          key: file.name,
          name: file.name,
          kind: "file" as const,
          lastModified: file.lastModified,
          size: file.size,
          href,
          actions: [downloadAction(() => downloadAll([href])), copyNameAction(file.name)],
        };
      });
      return [...subfolders, ...fileRows];
    }
    // LEEFT_TO_PARTNER
    if (!outbound) {
      return [];
    }
    // See the eslint-disable above rootRows.
    // eslint-disable-next-line react-hooks/refs
    const subfolders: ExplorerRow[] = outbound.folders.map((info) => {
      const childPath = joinPath(path, info.name);
      return {
        key: `folder:${info.name}`,
        name: info.name,
        kind: "folder" as const,
        lastModified: null,
        size: null,
        onActivate: () => setPath(childPath),
        actions: buildFolderActions("LEEFT_TO_PARTNER", childPath, false),
      };
    });
    const fileRows: ExplorerRow[] = outbound.outbound.map((file) => {
      const href = apiClient.staffDownloadUrl(code, "outbound", file.name, path);
      return {
        key: file.name,
        name: file.name,
        kind: "file" as const,
        lastModified: file.lastModified,
        size: file.size,
        href,
        actions: [
          downloadAction(() => downloadAll([href])),
          deleteAction(() => setDeleteTarget(file.name)),
          copyNameAction(file.name),
        ],
      };
    });
    return [...subfolders, ...fileRows];
  }, [mount, received, outbound, path, apiClient, code, setPath, buildFolderActions]);

  const displayedRows = useMemo(() => {
    const rows = folder ? folderRows : rootRows;
    return sortRows(filterRowsByName(rows, search), sort, dir);
  }, [folder, folderRows, rootRows, search, sort, dir]);

  const selectedRows = displayedRows.filter((row) => selected.has(row.key));
  const canDownloadSelected = anyRowHasAction(selectedRows, "download");
  const canDeleteSelected = anyRowHasAction(selectedRows, "delete");
  const showDeleteAction = anyRowHasAction(folderRows, "delete");
  const folderLabel = FOLDERS.find((f) => f.key === folder)?.label ?? null;

  // See the eslint-disable above rootRows: triggerFileInput is only
  // invoked later, from the pane menu's onClick.
  /* eslint-disable react-hooks/refs */
  const paneActions: RowAction[] = [
    ...(uploadEnabled ? [uploadHereAction(triggerFileInput, "Upload file")] : []),
    ...(newFolderEnabled ? [newFolderAction(() => setNewFolderParent(path))] : []),
    refreshAction(refreshAll),
  ];
  /* eslint-enable react-hooks/refs */
  const treeFolderActions = new Map(rootRows.map((row) => [row.key, row.actions ?? []]));

  return (
    <ExplorerLayout
      sidebar={
        <FileTree
          folders={FOLDERS}
          selectedFolder={folder}
          selectedPath={path}
          onSelectRoot={() => setFolder(null)}
          onSelect={(nextFolder, nextPath) => navigate(nextFolder, nextPath)}
          loadChildren={(folderKey, at) =>
            (folderKey === "LEEFT_TO_PARTNER"
              ? apiClient.getSharingOutbound(code, at)
              : apiClient.getSharingDelivered(code, at)
            ).then((response) => response.folders.map((info) => info.name))
          }
          refreshKey={detail}
          actionsForNode={(folderKey, at) => {
            if (folderKey === null) {
              return [refreshAction(refreshAll)];
            }
            if (at === "") {
              return treeFolderActions.get(folderKey) ?? [];
            }
            return buildFolderActions(folderKey as FolderKey, at, false);
          }}
        />
      }
    >
      <ExplorerToolbar
        uploadEnabled={uploadEnabled}
        onUpload={triggerFileInput}
        newFolderEnabled={folder === "LEEFT_TO_PARTNER" ? newFolderEnabled : undefined}
        onNewFolder={folder === "LEEFT_TO_PARTNER" ? () => setNewFolderParent(path) : undefined}
        canDownloadSelected={canDownloadSelected}
        canDeleteSelected={canDeleteSelected}
        showDeleteAction={showDeleteAction}
        onDownloadSelected={() => runSelectedActions(displayedRows, selected, "download")}
        onDeleteSelected={() => {
          // Deleting more than one selected file still confirms once per
          // file (ConfirmDialog only tracks a single target); the first
          // selected deletable row starts that flow, same as before.
          const first = selectedRows.find((row) => row.actions?.some((action) => action.type === "delete"));
          if (first) {
            setDeleteTarget(first.name);
          }
        }}
        onRefresh={refreshAll}
        search={search}
        onSearchChange={setSearch}
        view={view}
        onToggleView={() => setView(view === "list" ? "grid" : "list")}
      />
      <input
        ref={fileInputRef}
        type="file"
        hidden
        aria-label="Choose a file to upload to this partner"
        onChange={handleFileInputChange}
      />

      <ExplorerUploadStatus
        upload={upload}
        error={uploadError}
        notice={uploadNotice}
        retryFile={retryFile}
        onCancel={cancel}
        onRetry={start}
      />

      <Box
        onDragOver={(event) => {
          event.preventDefault();
          if (uploadEnabled && !upload) {
            setDragOver(true);
          }
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onContextMenu={paneMenu.open}
        sx={{
          flexGrow: 1,
          px: 2,
          pb: 2,
          outline: dragOver ? "2px dashed" : "none",
          outlineColor: "primary.main",
          outlineOffset: -4,
        }}
      >
        <ExplorerBreadcrumbs
          rootLabel="Your Files"
          folderLabel={folderLabel}
          path={path}
          onNavigateRoot={() => setFolder(null)}
          onNavigateFolder={() => setPath("")}
          onNavigatePath={setPath}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {folder === "PARTNER_TO_LEEFT" && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Files this partner sent, after they passed the malware scan.
          </Typography>
        )}
        {folder === "LEEFT_TO_PARTNER" && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Files staff have shared with this partner. Partners download from here; staff can
            upload new files or remove ones no longer needed.
          </Typography>
        )}

        {view === "grid" ? (
          <ExplorerGrid
            caption={folder ? `${folderLabel} files` : "Your Files"}
            rows={displayedRows}
            loading={loading}
            emptyMessage={
              folder === "PARTNER_TO_LEEFT"
                ? "No files delivered by this partner yet."
                : folder === "LEEFT_TO_PARTNER"
                  ? "No files shared with this partner yet."
                  : "No folders yet."
            }
            selectedKeys={folder ? selected : undefined}
            onToggleSelect={folder ? toggleSelect : undefined}
          />
        ) : (
          <ExplorerTable
            caption={folder ? `${folderLabel} files` : "Your Files"}
            rows={displayedRows}
            loading={loading}
            emptyMessage={
              folder === "PARTNER_TO_LEEFT"
                ? "No files delivered by this partner yet."
                : folder === "LEEFT_TO_PARTNER"
                  ? "No files shared with this partner yet."
                  : "No folders yet."
            }
            sort={sort}
            dir={dir}
            onSort={toggleSort}
            selectedKeys={folder ? selected : undefined}
            onToggleSelect={folder ? toggleSelect : undefined}
            onToggleSelectAll={
              folder
                ? () =>
                    setSelected((current) =>
                      current.size === displayedRows.length ? new Set() : new Set(displayedRows.map((r) => r.key)),
                    )
                : undefined
            }
          />
        )}
      </Box>

      <UploadRejectedDialog file={uploadRejected} onClose={dismissRejected} />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete file"
        description={`This deletes "${deleteTarget}" from what the partner can download. This cannot be undone.`}
        confirmLabel="Delete file"
        destructive
        error={deleteError}
        onConfirm={() => handleDeleteConfirmed()}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteError(null);
        }}
      />

      <NewFolderDialog
        open={newFolderParent !== null}
        parentPath={newFolderParent ?? ""}
        onClose={() => setNewFolderParent(null)}
        onCreate={handleCreateFolder}
      />

      <ConfirmDialog
        open={deleteFolderTarget !== null}
        title="Delete folder"
        description={`This deletes the empty folder "${deleteFolderTarget}". This cannot be undone.`}
        confirmLabel="Delete folder"
        destructive
        error={deleteFolderError}
        onConfirm={() => handleDeleteFolderConfirmed()}
        onClose={() => {
          setDeleteFolderTarget(null);
          setDeleteFolderError(null);
        }}
      />

      {uploadEnabled === false && folder === "LEEFT_TO_PARTNER" && detail.closed && (
        <Alert severity="warning" sx={{ mx: 2, mb: 2 }}>
          This sharing is closed; add a member to reopen it before uploading.
        </Alert>
      )}

      <Menu
        open={paneMenu.position !== null}
        onClose={paneMenu.close}
        anchorReference="anchorPosition"
        anchorPosition={paneMenu.position ?? undefined}
      >
        {paneActions.map((action) => (
          <MenuItem
            key={action.key}
            onClick={() => {
              paneMenu.close();
              action.onSelect();
            }}
          >
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </ExplorerLayout>
  );
}
